import { createContext, useCallback, useContext, useState } from 'react';
import { getPreference, setValuesHidden } from 'utils/privacy';

interface PrivacyContextValue {
  valuesHidden: boolean;
  toggleValues: () => void;
}

const PrivacyContext = createContext<PrivacyContextValue>({
  valuesHidden: false,
  toggleValues: () => {},
});

/**
 * Estado do modo privacidade.
 *
 * Só guarda o booleano e o setter: quem realmente esconde o dinheiro é `maskCurrency`,
 * lendo a flag de módulo em `utils/privacy`. O hook precisa ser chamado por um componente
 * que renderize a árvore inteira no próprio JSX (hoje `layouts/access`) — um provider que
 * recebe `children` por prop não serviria, porque a identidade estável desses elementos faz
 * o React pular a re-renderização e os valores antigos ficariam congelados na tela.
 */
export const usePrivacyState = (): PrivacyContextValue => {
  const [valuesHidden, setHidden] = useState(getPreference);

  // O updater do useState precisa ser puro: o StrictMode o executa duas vezes.
  const toggleValues = useCallback(() => {
    const next = !getPreference();
    setValuesHidden(next);
    setHidden(next);
  }, []);

  return { valuesHidden, toggleValues };
};

export const PrivacyProvider = PrivacyContext.Provider;

export const usePrivacy = () => useContext(PrivacyContext);

export default PrivacyContext;
